import React, { useMemo, useState } from "react";
import {
  Box,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Typography,
  Divider,
} from "@mui/material";
import IconButton from "@mui/material/IconButton";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import FieldText from "./FieldText";
import FieldNumber from "./FieldNumber";
import FieldBoolean from "./FieldBoolean";
import FieldSelect from "./FieldSelect";
import FieldDate from "./FieldDate";
import FieldObject from "./FieldObject";

function humanize(key) {
  if (!key) return "";
  return key
    .toString()
    .replace(/_/g, " ")
    .replace(/\b\w/g, (c) => c.toUpperCase());
}

export default function FieldArray({
  name,
  schema = {},
  value = [],
  onChange = () => {},
}) {
  const groupName =
    schema.groupName || schema.group_name || schema.group || "item";
  const itemFields = useMemo(() => Object.keys(schema.fields || {}), [schema]);
  const items = Array.isArray(value) ? value : [];

  const [open, setOpen] = useState(false);
  const [draft, setDraft] = useState({});

  const openDialog = () => {
    setDraft({});
    setOpen(true);
  };
  const closeDialog = () => setOpen(false);

  const setDraftField = (ik, v) => {
    setDraft((prev) => ({ ...prev, [ik]: v }));
  };

  const setDraftNestedObject = (ik, jk, v) => {
    setDraft((prev) => ({
      ...prev,
      [ik]: { ...(prev[ik] || {}), [jk]: v },
    }));
  };

  const addItem = () => {
    const next = [...items, draft];
    onChange(next);
    setOpen(false);
  };

  const removeItem = (idx) => {
    const next = items.filter((_, i) => i !== idx);
    onChange(next);
  };

  const renderFieldInput = (ik) => {
    const idef = schema.fields[ik] || {};
    const itype = idef.type;
    const v = draft[ik];

    if (itype === "number") {
      return (
        <FieldNumber
          key={ik}
          name={ik}
          schema={idef}
          value={v}
          onChange={(nv) => setDraftField(ik, nv)}
        />
      );
    }
    if (itype === "string") {
      return (
        <FieldText
          key={ik}
          name={ik}
          schema={idef}
          value={v}
          onChange={(nv) => setDraftField(ik, nv)}
        />
      );
    }
    if (itype === "boolean") {
      return (
        <FieldBoolean
          key={ik}
          name={ik}
          schema={idef}
          value={v}
          onChange={(nv) => setDraftField(ik, nv)}
        />
      );
    }
    if (itype === "select") {
      return (
        <FieldSelect
          key={ik}
          name={ik}
          schema={idef}
          value={v}
          onChange={(nv) => setDraftField(ik, nv)}
        />
      );
    }
    if (itype === "date") {
      return (
        <FieldDate
          key={ik}
          name={ik}
          schema={idef}
          value={v}
          onChange={(nv) => setDraftField(ik, nv)}
        />
      );
    }
    if (itype === "object" && idef.fields) {
      const current = v && typeof v === "object" && !Array.isArray(v) ? v : {};
      return (
        <Box key={ik} sx={{ mt: 1 }}>
          <Typography variant="body2" sx={{ fontWeight: 500, mb: 0.5 }}>
            {humanize(ik)}
          </Typography>
          <FieldObject
            name={ik}
            fields={idef.fields}
            getValue={(jk) =>
              current && jk in current ? current[jk] : undefined
            }
            onChange={(jk, nv) => setDraftNestedObject(ik, jk, nv)}
          />
        </Box>
      );
    }

    return (
      <Box key={ik} mb={1} sx={{ maxWidth: 360 }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <Typography variant="body2" sx={{ fontWeight: 500 }}>
            {humanize(ik)}:
          </Typography>
          <Typography variant="body2" color="text.primary">
            —
          </Typography>
        </Box>
        {idef.description && (
          <Typography variant="caption" color="text.secondary">
            {idef.description}
          </Typography>
        )}
      </Box>
    );
  };

  return (
    <Box sx={{ py: 0.75 }}>
      <Box
        sx={{
          display: "flex",
          gap: 1,
          alignItems: "baseline",
          flexWrap: "wrap",
        }}
      >
        <Typography
          variant="body2"
          sx={{
            minWidth: { xs: 120, sm: 160, md: 220 },
            fontWeight: 500,
            fontSize: { xs: "0.875rem", sm: "0.9rem" },
          }}
        >
          {humanize(name)}:
        </Typography>
        <Box sx={{ flex: 1 }} />
        <Button
          variant="contained"
          size="small"
          onClick={openDialog}
          sx={{ mr: { xs: 0, sm: 1, md: 2 } }}
        >
          Add {humanize(groupName)}
        </Button>
      </Box>
      {schema.description && (
        <Typography variant="caption" color="text.secondary">
          {schema.description}
        </Typography>
      )}

      {items.length > 0 && (
        <Box sx={{ pl: 3, mt: 1 }}>
          {items.map((item, idx) => (
            <Box
              key={idx}
              sx={{
                mb: 1,
                pl: 1,
                borderLeft: "1px solid",
                borderColor: "divider",
              }}
            >
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                }}
              >
                <Typography
                  variant="subtitle2"
                  sx={{ fontWeight: 500, mb: 0.5 }}
                >
                  {humanize(groupName)} {idx + 1}
                </Typography>
                <IconButton
                  aria-label="remove"
                  size="small"
                  onClick={() => removeItem(idx)}
                >
                  <DeleteOutlineIcon fontSize="small" />
                </IconButton>
              </Box>
              {itemFields.map((ik) => {
                const idef = schema.fields[ik];
                const itype = idef && idef.type;
                const ilabel = (idef && idef.label) || humanize(ik);
                const ivalue = item && item[ik];
                return (
                  <Box key={ik} sx={{ py: 0.25 }}>
                    <Box
                      sx={{
                        display: "flex",
                        gap: 1,
                        alignItems: "baseline",
                        flexWrap: "wrap",
                      }}
                    >
                      <Typography
                        variant="body2"
                        sx={{
                          minWidth: { xs: 110, sm: 150, md: 200 },
                          fontSize: { xs: "0.875rem", sm: "0.9rem" },
                        }}
                      >
                        {ilabel}:
                      </Typography>
                      <Typography
                        variant="body2"
                        color="text.primary"
                        sx={{
                          flex: 1,
                          fontSize: { xs: "0.875rem", sm: "0.9rem" },
                          wordBreak: "break-word",
                        }}
                      >
                        {ivalue === null ||
                        ivalue === undefined ||
                        ivalue === ""
                          ? "—"
                          : String(ivalue)}
                      </Typography>
                    </Box>
                    {idef && idef.description && (
                      <Typography variant="caption" color="text.secondary">
                        {idef.description}
                      </Typography>
                    )}
                  </Box>
                );
              })}
            </Box>
          ))}
        </Box>
      )}

      <Dialog open={open} onClose={closeDialog} fullWidth maxWidth="sm">
        <DialogTitle>Add {humanize(groupName)}</DialogTitle>
        <DialogContent dividers>
          <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
            {itemFields.map((ik) => (
              <Box key={ik}>{renderFieldInput(ik)}</Box>
            ))}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeDialog}>Cancel</Button>
          <Button variant="contained" onClick={addItem}>
            Add
          </Button>
        </DialogActions>
      </Dialog>
      <Divider sx={{ mt: 1 }} />
    </Box>
  );
}
